/**
 * autismObsRouting.js
 *
 * Pure helpers for the Adult Autism Behavioural Observation (OBS) module,
 * split across four routed appointment phases:
 *
 *   1. Task Administration — administer tasks and capture master notes
 *      (+ Self-Report).
 *   2. ADOS-informed coding — score ADOS-2 Module 4 items.
 *   3. Criterion Mapping — map notes to DSM-5 criteria and record evidence.
 *   4. Review and Synthesis — evidence tables, formulation, confidence, finish.
 *
 * Grey ModularPhaseSkipLink footers jump between these phases; primary CTAs
 * step within a phase (tasks / Self-Report). Same hierarchy as History ↔ SSI
 * ↔ Review.
 *
 * These functions centralise route paths, progress computation, resume
 * routing, and sidebar items so the pages, the Patient Overview card, and
 * the unit tests all agree. Everything reads from `assessmentStages` on the
 * modular subcollection data model — no legacy dataModel.
 */

import { ADULT_ASD_OBSERVATION_TASKS } from '../pages/providers/12_observation/config/observationTasksConfig';
import { ADOS_ALL_ITEMS } from '../pages/providers/12_observation/config/adosConfig';
import {
  areAllObservationCriteriaComplete,
  hasAnyCriteriaEvidenceDecision,
  countDecidedCriteriaEvidence,
  OBS_EVIDENCE_CRITERION_COUNT,
} from './observationCriteriaCompletion';
import {
  getAdministeredTaskIds,
  isObservationTaskAdministered,
} from './observationTaskAdministration';

// ─── Route helpers ──────────────────────────────────────────────────────────

/** Builds the OBS module base path for a patient (no trailing slash). */
export const getAutismOBSBasePath = (patientId) =>
  `/patient/${patientId}/proforma/adult-asd/observation`;

/**
 * Returns the absolute path for each OBS phase, keyed by step id.
 * `synthesis` is kept as a legacy alias of `adosCoding` so old bookmarks and
 * deep links still resolve after the Synthesis page was split into ADOS +
 * Criterion Mapping.
 */
export const getAutismOBSStepRoutes = (patientId) => {
  const base = getAutismOBSBasePath(patientId);
  const adosCoding = `${base}/ados-coding`;
  return {
    taskAdministration: `${base}/task-administration`,
    adosCoding,
    criterionMapping: `${base}/criterion-mapping`,
    review: `${base}/review`,
    /** @deprecated Use adosCoding — kept for backward-compatible deep links. */
    synthesis: adosCoding,
  };
};

// ─── Step titles ─────────────────────────────────────────────────────────────

export const AUTISM_OBS_STEP_TITLES = {
  taskAdministration: 'Task Administration',
  adosCoding: 'ADOS-Informed Coding',
  criterionMapping: 'Criterion Mapping',
  review: 'Review and Synthesis',
};

/** The 4 phase ids in clinical order. */
export const AUTISM_OBS_ORDERED_STEP_IDS = [
  'taskAdministration',
  'adosCoding',
  'criterionMapping',
  'review',
];

// ─── Per-phase completeness ──────────────────────────────────────────────────

/**
 * Task Administration is "complete" when every selected task has at least one
 * master note recorded. Matches the original single-page progress logic.
 *
 * @param {Object} data - assessmentStages.observation.data
 * @returns {boolean}
 */
export function isTaskAdministrationComplete(data) {
  if (!Array.isArray(data?.selectedTaskIds) || data.selectedTaskIds.length === 0) return false;
  const allTaskData = data?.allTaskData || {};
  // Only administered tasks need master notes. If every task was marked
  // "Not administered" there is nothing to complete → not complete.
  const taskIds = getAdministeredTaskIds(data.selectedTaskIds, allTaskData);
  if (taskIds.length === 0) return false;
  return taskIds.every((id) => {
    const td = allTaskData[id];
    return td?.masterNotes && td.masterNotes.length > 0;
  });
}

/**
 * ADOS-informed coding is "complete" when every Module 4 item has a score.
 * ADOS is optional for the assessment — this is used only for resume routing
 * and nav badges, never for the dashboard progress fraction.
 *
 * @param {Object} data - assessmentStages.observation.data
 * @returns {boolean}
 */
export function isAdosCodingComplete(data) {
  const adosData = data?.adosData || {};
  if (ADOS_ALL_ITEMS.length === 0) return false;
  return ADOS_ALL_ITEMS.every(
    (item) => adosData[`ados_${item.id}_score`] != null,
  );
}

/**
 * True when the clinician has started Criterion Mapping on any selected task
 * (an evidence decision or at least one mapped criterion note). Used so
 * Overview "Continue" can skip past ADOS once mapping is underway.
 *
 * @param {Object} data - assessmentStages.observation.data
 * @returns {boolean}
 */
export function hasCriterionMappingStarted(data) {
  // New criteria-level evidence map — any decision counts as "started".
  const criteriaEvidence = data?.criteriaEvidence;
  if (criteriaEvidence && typeof criteriaEvidence === 'object') {
    if (Object.values(criteriaEvidence).some(Boolean)) return true;
  }

  const taskIds = data?.selectedTaskIds;
  if (!Array.isArray(taskIds) || taskIds.length === 0) return false;
  const allTaskData = data?.allTaskData || {};

  for (const id of taskIds) {
    const td = allTaskData[id];
    if (!td || typeof td !== 'object') continue;
    // Stale mapping data on a "Not administered" task must not make Overview
    // "Continue" resume at Criterion Mapping.
    if (!isObservationTaskAdministered(td)) continue;
    for (const key of Object.keys(td)) {
      if (/^dsm_.+_evidence$/.test(key) && td[key]) return true;
      if (
        /^dsm_.+_notes$/.test(key)
        && Array.isArray(td[key])
        && td[key].some((n) => n && String(n.text || '').trim())
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Criterion Mapping is "complete" when every DSM criterion (A1–B4) has an
 * evidence decision on `criteriaEvidence`, or — for legacy assessments —
 * when every selected administered task has all of its criteria decided.
 *
 * @param {Object} data - assessmentStages.observation.data
 * @returns {boolean}
 */
export function isCriterionMappingComplete(data) {
  const taskIds = data?.selectedTaskIds;
  if (!Array.isArray(taskIds) || taskIds.length === 0) return false;
  return areAllObservationCriteriaComplete(
    taskIds,
    ADULT_ASD_OBSERVATION_TASKS,
    data?.allTaskData || {},
    data?.criteriaEvidence || null,
  );
}

/**
 * @deprecated Prefer isCriterionMappingComplete — kept so older call sites /
 * tests that still say "synthesis" keep working.
 */
export function isSynthesisComplete(data) {
  return isCriterionMappingComplete(data);
}

/**
 * Review is "complete" when a ternary confidence rating has been recorded.
 *
 * @param {Object} review - assessmentStages.observation.review
 * @returns {boolean}
 */
export function isReviewComplete(review) {
  return review?.confidenceTernary != null;
}

// ─── Progress ────────────────────────────────────────────────────────────────

/**
 * Fraction (0..1) of selected tasks that have at least one master note.
 * This is the Task Administration slice only — it feeds the first third of
 * `computeAutismOBSProgress`, which is what actually gets persisted.
 *
 * @param {Object} data - The observation stage data object
 * @returns {number}
 */
export function computeTaskAdministrationProgress(data) {
  if (!data) return 0;
  if (!Array.isArray(data.selectedTaskIds) || data.selectedTaskIds.length === 0) return 0;

  const taskData = data.allTaskData || {};
  // Administered-only numerator AND denominator: a "Not administered" task
  // neither counts as done nor dilutes the fraction. When every task is
  // flagged there is nothing to administer → 0 (never divide by zero).
  const taskIds = getAdministeredTaskIds(data.selectedTaskIds, taskData);
  if (taskIds.length === 0) return 0;

  let withNotes = 0;
  for (const id of taskIds) {
    const td = taskData[id];
    if (!td) continue;
    if (td.masterNotes && td.masterNotes.length > 0) withNotes++;
  }
  return withNotes / taskIds.length;
}

/**
 * Fraction (0..1) of the Criterion Mapping third that is done.
 *
 * Data-driven dual path (no workflow-config flag needed — a v1 patient can
 * never author `criteriaEvidence`, so its presence is a truthful signal of the
 * v2 flow):
 *   • v2 — once ANY criteria-level decision exists, award this third pro-rata
 *     over the seven DSM criteria (A1–B4). The pill then creeps up per decision
 *     and reaches exactly 1 only when all seven are decided (so it still agrees
 *     with isCriterionMappingComplete at the boundaries).
 *   • v1 / legacy — no `criteriaEvidence` map, so keep the pre-redesign
 *     all-or-nothing per-task check: the third is 0 until every administered
 *     task has all of its criteria decided, then 1.
 *
 * @param {Object} data - assessmentStages.observation.data
 * @returns {number} 0..1
 */
export function computeCriterionMappingProgress(data) {
  const criteriaEvidence = data?.criteriaEvidence || null;
  if (hasAnyCriteriaEvidenceDecision(criteriaEvidence)) {
    return countDecidedCriteriaEvidence(criteriaEvidence) / OBS_EVIDENCE_CRITERION_COUNT;
  }
  return isCriterionMappingComplete(data) ? 1 : 0;
}

/**
 * OBS progress as a fraction (0..1) built from three equal thirds:
 *   1/3 — Task Administration (pro-rata: fraction of tasks with masterNotes)
 *   1/3 — Criterion Mapping (v2: pro-rata over the seven criteria decisions;
 *         v1: all-or-nothing every criterion decided — see
 *         computeCriterionMappingProgress)
 *   1/3 — Review (all-or-nothing: confidence rating recorded)
 *
 * The Task Administration and (in v2) Criterion Mapping thirds are awarded
 * pro-rata rather than all-or-nothing so the dashboard pill creeps up as the
 * clinician works, instead of sitting at a step boundary until the very last
 * input lands. Review is genuinely binary, so it stays all-or-nothing.
 *
 * ADOS-informed coding, note tagging, additional criterion notes, and Other
 * Behaviours are intentionally omitted — they are optional/encouraged, so
 * counting them would stall the pill behind work the clinician may never do.
 * A completed stage always reports 1.
 *
 * This is the single source of truth for OBS progress: it is what the proforma
 * persists to `assessmentStages.observation.progress` and what Overview falls
 * back to before the first write lands. Critically, it can only reach 1 once
 * mapping AND review are done, so a 100% pill genuinely means "finished" —
 * Overview keys its "Review" call-to-action off that.
 *
 * @param {Object} params
 * @param {Object} params.data - assessmentStages.observation.data
 * @param {Object} params.review - assessmentStages.observation.review
 * @param {string} [params.status] - stage status; 'completed' forces 1
 * @returns {number}
 */
export function computeAutismOBSProgress({ data, review, status }) {
  if (status === 'completed') return 1;

  // Reaches exactly 1 when every administered task has notes, so this
  // subsumes isTaskAdministrationComplete for the purposes of the fraction.
  let thirds = computeTaskAdministrationProgress(data);
  thirds += computeCriterionMappingProgress(data);
  if (isReviewComplete(review)) thirds += 1;
  return thirds / 3;
}

// ─── Resume routing ──────────────────────────────────────────────────────────

/**
 * Which OBS phase "Start / Continue" should land on.
 *
 *   1. Task Administration until every selected task has notes
 *   2. Criterion Mapping once ADOS is fully scored OR mapping has started
 *      (so clinicians mid-mapping are not bounced back to ADOS)
 *   3. Otherwise ADOS (entry to the post-session coding block)
 *   4. Review once every criterion has an evidence decision
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {Object|undefined} params.assessmentStages
 * @returns {string} Absolute route path
 */
export function getAutismOBSStartRoute({ patientId, assessmentStages }) {
  const routes = getAutismOBSStepRoutes(patientId);
  const stage = assessmentStages?.observation;
  const data = stage?.data || {};

  if (!isTaskAdministrationComplete(data)) return routes.taskAdministration;
  if (!isCriterionMappingComplete(data)) {
    if (isAdosCodingComplete(data) || hasCriterionMappingStarted(data)) {
      return routes.criterionMapping;
    }
    return routes.adosCoding;
  }
  return routes.review;
}

// ─── Sidebar nav items ───────────────────────────────────────────────────────

/**
 * Maps an OBS sidebar section index (0–3) to its route.
 *
 * @param {string} patientId
 * @param {number} index - zero-based sidebar index
 * @returns {string} absolute route path
 */
export function getAutismOBSRouteForSectionIndex(patientId, index) {
  const routes = getAutismOBSStepRoutes(patientId);
  const ordered = [
    routes.taskAdministration,
    routes.adosCoding,
    routes.criterionMapping,
    routes.review,
  ];
  return ordered[index] || routes.taskAdministration;
}

/**
 * Builds nav items describing the four OBS appointment phases.
 *
 * Not currently mounted in the UI — cross-phase jumps use grey
 * ModularPhaseSkipLink footers (same pattern as History ↔ SSI ↔ Review).
 * Kept (and unit-tested) so a future top-level phase sidebar can reuse the
 * same unlock / completed semantics without re-deriving them.
 *
 * Task Administration, ADOS, and Criterion Mapping are always unlocked.
 * Review stays gated until every criterion has an evidence decision.
 *
 * @param {Object} params
 * @param {Object|undefined} params.assessmentStages
 * @param {number} params.currentSectionIndex - zero-based active page index
 * @returns {{items: Array, orderedIds: string[], activeId: string}}
 */
export function buildAutismOBSNavItems({ assessmentStages, currentSectionIndex }) {
  const stage = assessmentStages?.observation;
  const data = stage?.data || {};
  const review = stage?.review;
  const stageComplete = stage?.status === 'completed';

  const taComplete = isTaskAdministrationComplete(data);
  const adosComplete = isAdosCodingComplete(data);
  const mappingComplete = isCriterionMappingComplete(data);
  const rvComplete = isReviewComplete(review);

  const items = [
    {
      id: 'taskAdministration',
      badge: 1,
      label: AUTISM_OBS_STEP_TITLES.taskAdministration,
      status: (taComplete || stageComplete) ? 'completed' : 'default',
      isUnlocked: true,
    },
    {
      id: 'adosCoding',
      badge: 2,
      label: AUTISM_OBS_STEP_TITLES.adosCoding,
      status: (adosComplete || stageComplete) ? 'completed' : 'default',
      isUnlocked: true,
    },
    {
      id: 'criterionMapping',
      badge: 3,
      label: AUTISM_OBS_STEP_TITLES.criterionMapping,
      status: (mappingComplete || stageComplete) ? 'completed' : 'default',
      isUnlocked: true,
    },
    {
      id: 'review',
      badge: 4,
      label: AUTISM_OBS_STEP_TITLES.review,
      status: (rvComplete || stageComplete) ? 'completed' : 'default',
      // Gated: every criterion must be decided before Review unlocks.
      isUnlocked: mappingComplete || stageComplete,
    },
  ];

  return {
    items,
    orderedIds: [...AUTISM_OBS_ORDERED_STEP_IDS],
    activeId: AUTISM_OBS_ORDERED_STEP_IDS[currentSectionIndex],
  };
}
